import { createContext, useContext, useState } from 'react';
import { useRef } from 'react';

import { MoreVerticalCircle01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import FeatherIcon from 'feather-icons-react';
import { useClickOutside } from 'hooks/click-outside';

import { IconComponent } from 'components/Icon/IconComponent';
import { IconButton } from 'components/IconButton/IconButton';

import styles from './ActionsMenu.module.scss';

const ActionsMenuContext = createContext(null);

const useActionsMenuContext = () => {
  const context = useContext(ActionsMenuContext);
  if (!context)
    throw new Error(
      `ActionsMenu compound components cannot be rendered outside the ActionsMenu component`,
    );
  return context;
};

export const ActionsMenu = ({
  children,
  className,
  small = false,
  newIconLibrary = false,
  triggerIcon,
  triggerClassName = 'u-margin-left',
  triggerDataTestId,
}) => {
  const ref = useRef(null);

  const [opened, setOpened] = useState(false);

  useClickOutside(ref, () => setOpened(false));

  const iconToUse = newIconLibrary ? (triggerIcon ?? MoreVerticalCircle01Icon) : null;

  return (
    <ActionsMenuContext.Provider value={{ setOpened }}>
      <div
        ref={ref}
        className={cn(styles.root, 'position-relative', 'd-flex', {
          [styles.rootOpened]: opened,
          [className]: className,
        })}>
        {newIconLibrary ? (
          <HugeiconsIcon
            icon={iconToUse}
            size={small ? '20' : '24'}
            onClick={() => setOpened(!opened)}
            data-testid={triggerDataTestId}
          />
        ) : (
          <IconButton
            small={small}
            icon="more-vertical"
            className={triggerClassName || undefined}
            onClick={() => setOpened(!opened)}
            dataTestId={triggerDataTestId}
          />
        )}
        <ul className={cn(styles.menu, 'card', 'card-with-shadow', 'card-with-border')}>
          {children}
        </ul>
      </div>
    </ActionsMenuContext.Provider>
  );
};

const Item = ({
  icon,
  title,
  disabled = false,
  onClick = () => {},
  newIconLibrary = false,
  type = 'secondary',
  dataTestId,
}) => {
  const { setOpened } = useActionsMenuContext();

  const handleClick = () => {
    if (disabled) return;
    onClick();
    setOpened(false);
  };

  return (
    <li
      className={cn(styles.item, { [styles.itemDisabled]: disabled })}
      onClick={handleClick}
      data-testid={dataTestId}>
      {newIconLibrary && icon ? (
        <IconComponent
          icon={icon}
          className="u-margin-right--small"
          noBackground
          type={type}
          size="small"
          strokeWidth={1.5}
        />
      ) : !newIconLibrary ? (
        <FeatherIcon className="u-margin-right--small" size={15} icon={icon} />
      ) : null}
      <span
        className={
          type === 'error' ? 'error-text' : type === 'success' ? 'text-success' : 'secondary-text'
        }>
        {title}
      </span>
    </li>
  );
};

ActionsMenu.Item = Item;
